import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Popup, useMap } from 'react-leaflet'

import type { OperatorId } from '../operations'

/** Ops that dock the root entity popup so map content around the pin stays visible. */
export const LOOK_AROUND_OPERATOR_IDS: ReadonlySet<OperatorId> = new Set([
  'nearby',
  'contains',
  'forecast',
  'eta',
])

export function operatorDocksPopupToCorner(operatorId: OperatorId | null | undefined): boolean {
  return Boolean(operatorId && LOOK_AROUND_OPERATOR_IDS.has(operatorId))
}

export function useMapOverlayHost(): HTMLElement | null {
  const map = useMap()
  const [host, setHost] = useState<HTMLElement | null>(null)
  useEffect(() => {
    setHost(map.getContainer().parentElement)
  }, [map])
  return host
}

/** Fixed corner panel so docked ops don't cover the area around the selected pin. */
export function DockedMapPopupShell({
  children,
  onDismiss,
}: {
  children: React.ReactNode
  onDismiss: () => void
}) {
  const map = useMap()
  const host = useMapOverlayHost()
  const onDismissRef = useRef(onDismiss)
  onDismissRef.current = onDismiss

  useEffect(() => {
    const onMapClick = () => {
      onDismissRef.current()
    }
    map.on('click', onMapClick)
    return () => {
      map.off('click', onMapClick)
    }
  }, [map])

  if (!host) return null
  return createPortal(
    <div
      className="pointer-events-auto absolute bottom-20 left-3 z-[1100] w-[min(20rem,calc(100%-1.5rem))] max-h-[min(52vh,26rem)] overflow-y-auto rounded-xl border-2 border-base-300 bg-base-100 p-3 shadow-xl md:bottom-4"
      role="dialog"
      aria-label="Entity operations"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    host,
  )
}

export function MapEntityPopupShell({
  docked,
  open,
  lockOpen,
  minWidth,
  onDismiss,
  children,
}: {
  docked: boolean
  open: boolean
  lockOpen: boolean
  minWidth?: number
  onDismiss: () => void
  children: React.ReactNode
}) {
  if (docked && open) {
    return <DockedMapPopupShell onDismiss={onDismiss}>{children}</DockedMapPopupShell>
  }
  return (
    <Popup
      minWidth={minWidth}
      maxWidth={340}
      autoPan={false}
      autoClose={!lockOpen}
      closeOnClick={!lockOpen}
    >
      {children}
    </Popup>
  )
}

